/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import FileIcon from '@atlaskit/icon/core/file';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { Card as SmartCard, SnippetBlock, TitleBlock } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { type Sources } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import {
	extractReadableTitleFromUrl,
	resolvesToReadableTitle,
	shouldSkipSmartCardResolution,
	stripSlackQueryParams,
	transformIfConfluenceLink,
} from '../../../../utils/url-transformer';
import { createFallbackComponent } from '../index';
import messages from '../messages';

const styles = cssMap({
	popupContent: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.150'),
		width: '330px',
		maxHeight: '400px',
		overflowY: 'auto',
	},
	popupHeader: {
		marginBottom: token('space.025'),
	},
	popupHeaderText: {
		paddingLeft: token('space.100'),
	},
	sourcesList: {
		paddingLeft: token('space.0'),
		listStyle: 'none',
		margin: 0,
	},
	sourceCard: {
		border: 'none',
		textAlign: 'left',
	},
	sourceListItem: {
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		cursor: 'pointer',
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	pdfLink: {
		display: 'flex',
		cursor: 'pointer',
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});

type SourcesPopupContentProps = {
	sources: Sources;
	onClickSourceHandler: (type: string, ari?: string, product?: string, index?: number) => void;
	onClickSource?: (
		type: string,
		citedProduct: string | null,
		product: string | null,
		index?: number,
	) => void;
	onClose: () => void;
	openSourceLinkInSameTab?: boolean;
	/** Optional function to strip datasource params and/or apply XPC URL wrapping. */
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
};

export const SourcesPopupContent = ({
	sources,
	onClickSourceHandler,
	onClickSource,
	onClose,
	openSourceLinkInSameTab = false,
	xpcWrapUrl,
}: SourcesPopupContentProps): JSX.Element => {
	di(SmartCard);
	const { formatMessage } = useIntl();

	const sourcesList = useMemo(() => {
		return sources.map(({ url, title, type, ari, product }, index) => {
			if (!url) {
				return null;
			}

			const xpcProcessedUrl = xpcWrapUrl ? xpcWrapUrl(url, false) : url;
			const processedUrl = stripSlackQueryParams(xpcProcessedUrl);
			const transformedUrl = processedUrl
				? transformIfConfluenceLink(processedUrl, true)
				: processedUrl;
			const originalTransformedUrl = transformIfConfluenceLink(xpcProcessedUrl, true);

			const hasReadableTitle = resolvesToReadableTitle(url, title);
			const displayTitle = hasReadableTitle ? title : extractReadableTitleFromUrl(url);

			const skipResolution = shouldSkipSmartCardResolution(url);

			const handleItemClick = (e: React.MouseEvent) => {
				onClickSourceHandler(type, ari, product, index);
				e.preventDefault();
				window.open(
					originalTransformedUrl,
					openSourceLinkInSameTab ? '_self' : '_blank',
					'noopener, noreferrer',
				);
			};

			const handleLiClick = (e: React.MouseEvent) => {
				onClickSourceHandler(type, ari, product, index);
			};

			const handlePressableClick = (e: React.MouseEvent) => {
				e.stopPropagation();
				handleItemClick(e);
			};

			const handleLiKeyDown = (e: React.KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					onClickSourceHandler(type, ari, product, index);
				}
			};

			return (
				<li
					key={index}
					onClick={skipResolution ? undefined : handleLiClick}
					onKeyDown={skipResolution ? undefined : handleLiKeyDown}
				>
					{skipResolution ? (
						<Pressable onClick={handlePressableClick} xcss={styles.sourceListItem}>
							<Tooltip content={displayTitle}>
								<Box xcss={styles.pdfLink}>
									<Inline space="space.075" alignBlock="center">
										<FileIcon size="medium" label="" />
										<Text size="medium" weight="regular" maxLines={1} color="color.link">
											{displayTitle}
										</Text>
									</Inline>
								</Box>
							</Tooltip>
						</Pressable>
					) : (
						<Box xcss={styles.sourceListItem}>
							<Box xcss={styles.sourceCard}>
								<SmartCard
									appearance="block"
									url={transformedUrl}
									showHoverPreview={false}
									fallbackComponent={createFallbackComponent(
										url,
										type,
										displayTitle,
										ari,
										product,
										onClickSource,
										true,
										index,
									)}
									ui={{
										hideBackground: true,
										hideElevation: true,
										hidePadding: true,
									}}
								>
									<TitleBlock maxLines={1} />
									<SnippetBlock maxLines={2} />
								</SmartCard>
							</Box>
						</Box>
					)}
				</li>
			);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sources]);

	const titleId = React.useId();

	return (
		<Box
			xcss={styles.popupContent}
			role="dialog"
			aria-label={formatMessage(messages.sourcesTitle, { sourceCount: sources.length })}
			aria-modal="false"
		>
			<Inline space="space.0" spread="space-between" alignBlock="center" xcss={styles.popupHeader}>
				<Box xcss={styles.popupHeaderText}>
					<Text size="medium" weight="medium" id={titleId}>
						{formatMessage(messages.sourcesTitle, { sourceCount: sources.length })}
					</Text>
				</Box>
				<IconButton
					appearance="subtle"
					icon={(props) => <CrossIcon {...props} size="small" />}
					label={formatMessage(messages.closeSourcesPopup)}
					shape="circle"
					onClick={onClose}
					autoFocus
				/>
			</Inline>
			<Stack as="ul" xcss={styles.sourcesList} space="space.050">
				{sourcesList}
			</Stack>
		</Box>
	);
};
